import { useCallback, useEffect, useMemo, useState } from "react";
import {
    useChat,
    useLocalParticipant,
    type ReceivedChatMessage,
} from "@livekit/components-react";

import {
    type MeetingChatEntry,
    type MeetingChatMessage,
    chatTimeline,
    normalizeChatBody,
    unreadCount,
} from "../domain/meetingChat";
import type { MeetingFileRejection } from "../domain/meetingFile";
import { useMeetingFiles } from "./useMeetingFiles";

export type MeetingChatChannel = {
    /** Messages and files, in the order they were sent. */
    entries: MeetingChatEntry[];
    /** How many of the other person's messages and files have not been seen. */
    unread: number;
    /** Whether the panel is up. Purely local — the chat takes nothing away. */
    open: boolean;
    setOpen: (open: boolean) => void;
    sending: boolean;
    /** True when the last message did not go out. */
    failed: boolean;
    send: (body: string) => void;
    sendFile: (file: File) => void;
    /** Why the last file picked was not sent, or null. */
    fileRejection: MeetingFileRejection | null;
};

/** LiveKit's message, in the shape the room's UI works in. */
function toMessage(
    raw: ReceivedChatMessage,
    localIdentity: string
): MeetingChatMessage {
    const authorId = raw.from?.identity ?? "";
    return {
        id: raw.id,
        timestamp: raw.timestamp,
        body: raw.message,
        authorId,
        authorName: raw.from?.name ?? "",
        mine: authorId !== "" && authorId === localIdentity,
    };
}

/**
 * Writing to the other person without interrupting them.
 *
 * A therapy session has moments where speaking is the wrong move: a link, a
 * name, a spelling, "your microphone is off", or a patient who has stopped
 * being able to say something out loud. Before this the only way to pass any
 * of that was to say it over whatever the other person was in the middle of.
 *
 * Built on LiveKit's own chat rather than a topic of our own. It is the same
 * data channel the whiteboard uses, so it costs the call nothing extra, and
 * the protocol already exists — inventing a second one would be inventing a
 * second set of bugs.
 *
 * Files ride along in the same panel (see `useMeetingFiles`), so a sheet
 * shows up next to the sentence that introduced it.
 *
 * Nothing is stored. The messages live in the two browsers holding the call
 * and end with it, which is the same bargain the whiteboard makes and the
 * right default for anything a patient says in a session.
 */
export function useMeetingChat(): MeetingChatChannel {
    const { chatMessages, send: sendChat, isSending } = useChat();
    const { localParticipant } = useLocalParticipant();
    const files = useMeetingFiles();
    const localIdentity = localParticipant?.identity ?? "";

    const [open, setOpen] = useState(false);
    const [failed, setFailed] = useState(false);
    /** How much of the conversation this side has had on screen. */
    const [readCount, setReadCount] = useState(0);

    const messages = useMemo(
        () => chatMessages.map((message) => toMessage(message, localIdentity)),
        [chatMessages, localIdentity]
    );

    // Anything on screen counts as read, including whatever arrives while the
    // panel stays open.
    const entries = useMemo(
        () => chatTimeline(messages, files.files),
        [messages, files.files]
    );

    useEffect(() => {
        if (open) setReadCount(entries.length);
    }, [open, entries.length]);

    const send = useCallback(
        (body: string) => {
            const text = normalizeChatBody(body);
            if (!text) return;
            setFailed(false);
            void sendChat(text).catch(() => setFailed(true));
        },
        [sendChat]
    );

    return {
        entries,
        unread: open ? 0 : unreadCount(entries, readCount),
        open,
        setOpen,
        sending: isSending,
        failed,
        send,
        sendFile: files.send,
        fileRejection: files.rejection,
    };
}
